import { PRIVACY_CONFIG } from "@/lib/privacy-config";

// Fetch and cache the PLONK proving assets (the witness wasm and the proving
// key). The private-pool proving key is about 215 MB, so we cache it at two levels:
// an in-memory Map for repeat proofs within a session, and the browser Cache
// Storage so it survives reloads and is downloaded only once per device. The
// first download reports progress; cache hits (memory or disk) are effectively
// instant. Cache Storage is used because the HTTP cache routinely refuses to
// persist a body this large, so a plain fetch re-downloads it on every reload.

// Bump whenever the served proving assets change. This drives BOTH the URL
// query string and the Cache Storage namespace so a returning device cannot
// reuse a stale build at any layer: the query busts the HTTP/edge cache (the
// storage route sets a 24h `max-age` and serves the same path), and the
// namespace busts our own Cache Storage copy. The storage route matches on the
// path splat only, so the query is ignored for object lookup.
//
// A bump is ALSO the remedy when the served object is correct but returning
// devices are stuck on a stale, WRONG-BUILD key cached from an earlier window
// (a prior redeploy changed the on-chain verifier and the served key was
// swapped to match, but this version was never rotated). Such a key has the
// right SIZE, so the integrity gate below cannot evict it, and the on-chain
// proof reverts as "did not verify". The runtime self-heal (`clearProvingCache`
// on that error) clears only our Cache Storage and then re-fetches the SAME
// URL, which the browser/edge HTTP cache can still answer with the old bytes,
// so it cannot rescue a client poisoned at the HTTP layer. Only changing the
// URL does. This build is the gross-amount 30 bps circuit. Do not publish this
// frontend build or replace the CDN objects until its matching verifier and
// fresh pools are deployed and configured together.
const ASSET_VERSION = "private-5f8b60bf-cors1";

function versionedAssetUrl(raw: string): string {
  if (!raw) return raw;
  const separator = raw.includes("?") ? "&" : "?";
  return `${raw}${separator}proof-build=${ASSET_VERSION}`;
}

export function wasmUrl(): string {
  return versionedAssetUrl(PRIVACY_CONFIG.wasmUrl);
}
export function zkeyUrl(): string {
  return versionedAssetUrl(PRIVACY_CONFIG.zkeyUrl);
}

// Exact served byte length of each proving asset. This is the integrity
// contract: a cached or freshly downloaded buffer whose length does not match
// is stale, truncated, or a wrong-power build, and must never be used to prove.
// A mismatched proving key still produces a structurally valid proof that fails
// on-chain verification (the "did not verify" failure), wasting the user's time
// and an on-chain attempt, so the buffer is evicted and re-fetched instead. The
// match is by path (origin- and query-independent). These move in lockstep with
// ASSET_VERSION: if the circuit or key is rebuilt and re-uploaded, bump
// ASSET_VERSION and update the sizes here together.
function expectedSize(url: string): number | null {
  if (url === zkeyUrl()) return 214900748;
  if (url === wasmUrl()) return 2585330;
  return null;
}

const cache = new Map<string, Uint8Array>();
const inflight = new Map<string, Promise<Uint8Array>>();

const ASSET_CACHE = `shielded-proving-v${ASSET_VERSION}`;

// Reports bytes downloaded so far and the total when it is known (0 if the
// server sent no content-length).
export type DownloadProgress = (loaded: number, total: number) => void;

async function openAssetCache(): Promise<Cache | null> {
  try {
    if (typeof caches === "undefined") return null;
    return await caches.open(ASSET_CACHE);
  } catch {
    return null;
  }
}

// Stream a Response body into one Uint8Array, reporting progress. Shared by the
// network path and the Cache Storage hit path so both surface a progress bar.
async function readWithProgress(
  res: Response,
  onProgress?: DownloadProgress,
  onActivity?: () => void,
): Promise<Uint8Array> {
  const total = Number(res.headers.get("content-length") || 0);
  const reader = res.body?.getReader();
  if (!reader) {
    const buf = new Uint8Array(await res.arrayBuffer());
    onActivity?.();
    onProgress?.(buf.length, buf.length || total);
    return buf;
  }

  // When the size is known, write chunks straight into one preallocated buffer
  // so the large payload is never held twice. Anything that does not fit
  // (unknown size, or a wrong content-length) spills into `extra` and is
  // assembled once at the end.
  const out = total > 0 ? new Uint8Array(total) : new Uint8Array(0);
  const extra: Uint8Array[] = [];
  let loaded = 0;
  // Throttle progress by TIME, not bytes. Each emit drives a setState that
  // re-renders the large dapp; on a fast connection a byte-based step still
  // fires often enough that the back-to-back re-renders saturate the main
  // thread and the browser shows "Page Unresponsive" mid-download. Capping to
  // ~5 updates a second keeps the progress bar live without starving the UI.
  let lastEmit = 0;
  const MIN_EMIT_MS = 200;
  for (;;) {
    const { done, value } = await reader.read();
    onActivity?.();
    if (done) break;
    if (loaded + value.length <= out.length) {
      out.set(value, loaded);
    } else {
      extra.push(value);
    }
    loaded += value.length;
    const now = Date.now();
    if (now - lastEmit >= MIN_EMIT_MS) {
      onProgress?.(loaded, total);
      lastEmit = now;
    }
  }
  onProgress?.(loaded, total);

  if (extra.length === 0 && loaded === out.length) return out;
  const assembled = new Uint8Array(loaded);
  const head = Math.min(out.length, loaded);
  assembled.set(out.subarray(0, head), 0);
  let offset = head;
  for (const chunk of extra) {
    assembled.set(chunk, offset);
    offset += chunk.length;
  }
  return assembled;
}

// Download size per range request. A single full-size response (the ~215MB
// proving key) is served fine by the origin but the deployment edge in front of
// custom domains aborts it mid-stream, surfacing to the browser as a 500. Each
// ranged chunk is a small, quick response the edge passes through cleanly, and
// the pieces are reassembled here into one contiguous buffer.
const CHUNK = 16 * 1024 * 1024;

// Read a response body into `out` starting at `writeAt`, reporting cumulative
// progress against `total`. Returns the offset just past the last byte written.
async function drainInto(
  res: Response,
  out: Uint8Array,
  writeAt: number,
  total: number,
  onProgress?: DownloadProgress,
  onActivity?: () => void,
): Promise<number> {
  const reader = res.body?.getReader();
  if (!reader) {
    const buf = new Uint8Array(await res.arrayBuffer());
    out.set(buf, writeAt);
    const end = writeAt + buf.length;
    onActivity?.();
    onProgress?.(end, total);
    return end;
  }
  let pos = writeAt;
  // Time-based throttle (see readWithProgress): cap progress emits so the
  // re-render they trigger cannot saturate the main thread on a fast stream.
  let lastEmit = 0;
  const MIN_EMIT_MS = 200;
  for (;;) {
    const { done, value } = await reader.read();
    onActivity?.();
    if (done) break;
    out.set(value, pos);
    pos += value.length;
    const now = Date.now();
    if (now - lastEmit >= MIN_EMIT_MS) {
      onProgress?.(pos, total);
      lastEmit = now;
    }
  }
  onProgress?.(pos, total);
  return pos;
}

// An inactivity watchdog for streamed downloads. A fetch to a stalled or dead
// upstream (for example, the storage service is down) otherwise hangs forever,
// freezing the proving-key download mid-progress with no error and no way out.
// The watchdog aborts the request when no bytes have arrived for `ms`; callers
// reset it on every read. Normal streams deliver data continuously, so this
// never trips a slow-but-flowing connection.
const STALL_MS = 45_000;
const MAX_ATTEMPTS = 3;

function isAbort(err: unknown): boolean {
  return err instanceof DOMException && err.name === "AbortError";
}

function withWatchdog(ms: number): {
  signal: AbortSignal;
  reset: () => void;
  done: () => void;
} {
  const controller = new AbortController();
  let timer: ReturnType<typeof setTimeout> | undefined;
  const reset = (): void => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(
      () => controller.abort(new DOMException("download stalled", "AbortError")),
      ms,
    );
  };
  const done = (): void => {
    if (timer) clearTimeout(timer);
  };
  reset();
  return { signal: controller.signal, reset, done };
}

// Fetch one byte range into `out` at `start`, guarded by the inactivity
// watchdog and retried a few times on transient failures. Each attempt
// re-requests the same range and writes from `start`, so a partial-then-retried
// chunk is idempotent. Returns the offset just past the chunk.
async function fetchChunkInto(
  url: string,
  start: number,
  end: number,
  out: Uint8Array,
  total: number,
  onProgress?: DownloadProgress,
): Promise<number> {
  let lastErr: unknown;
  for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
    const wd = withWatchdog(STALL_MS);
    try {
      const res = await fetch(url, {
        headers: { Range: `bytes=${start}-${end}` },
        signal: wd.signal,
      });
      if (res.status !== 206) {
        throw new Error(`could not download proving asset (${res.status})`);
      }
      return await drainInto(res, out, start, total, onProgress, wd.reset);
    } catch (err) {
      lastErr = err;
    } finally {
      wd.done();
    }
    if (attempt < MAX_ATTEMPTS) {
      await new Promise((r) => setTimeout(r, 500 * attempt));
    }
  }
  if (isAbort(lastErr)) {
    throw new Error("could not download proving asset (timed out)");
  }
  throw lastErr instanceof Error
    ? lastErr
    : new Error("could not download proving asset");
}

// Download a URL in sequential byte ranges. A cheap one-byte probe discovers
// the total size (from Content-Range) and whether ranges are honored; if the
// server responds 200 instead, the whole body is read in one guarded pass. All
// requests run under the inactivity watchdog so a dead upstream fails fast with
// an honest error instead of an indefinite freeze.
async function fetchRanged(
  url: string,
  onProgress?: DownloadProgress,
): Promise<Uint8Array> {
  const probeWd = withWatchdog(STALL_MS);
  let probe: Response;
  try {
    probe = await fetch(url, {
      headers: { Range: "bytes=0-0" },
      signal: probeWd.signal,
    });
  } catch (err) {
    throw isAbort(err)
      ? new Error("could not download proving asset (timed out)")
      : new Error("could not download proving asset (network)");
  } finally {
    probeWd.done();
  }

  // The server does not honor ranges: read the whole body in one guarded pass.
  if (probe.status === 200) {
    const wd = withWatchdog(STALL_MS);
    try {
      return await readWithProgress(probe, onProgress, wd.reset);
    } finally {
      wd.done();
    }
  }
  if (probe.status !== 206) {
    throw new Error(`could not download proving asset (${probe.status})`);
  }
  // Discard the one-byte probe body; the real bytes come from the chunk loop.
  try {
    await probe.body?.cancel();
  } catch {
    // ignore: cancelling the tiny probe body is best-effort.
  }
  const contentRange = probe.headers.get("content-range") || "";
  const total = Number(contentRange.split("/")[1] || 0);
  if (!Number.isFinite(total) || total <= 0) {
    // A 206 with no usable total would leave us guessing the asset length and
    // could silently truncate it; fail loudly instead.
    throw new Error("could not download proving asset (bad range response)");
  }

  const out = new Uint8Array(total);
  let offset = 0;
  while (offset < total) {
    const end = Math.min(offset + CHUNK - 1, total - 1);
    offset = await fetchChunkInto(url, offset, end, out, total, onProgress);
  }
  return out;
}

async function fetchBytes(url: string, onProgress?: DownloadProgress): Promise<Uint8Array> {
  const cached = cache.get(url);
  if (cached) {
    onProgress?.(cached.length, cached.length);
    return cached;
  }
  const existing = inflight.get(url);
  if (existing) return existing;

  const job = (async () => {
    const store = await openAssetCache();

    // Disk cache hit: serve the persisted copy, no network. Still reports
    // progress so the UI shows the same bar (it just fills near-instantly).
    if (store) {
      try {
        const hit = await store.match(url);
        if (hit && hit.ok) {
          const bytes = await readWithProgress(hit, onProgress);
          // Integrity gate: a persisted copy whose length does not match the
          // expected asset is stale, truncated, or a wrong build. Trusting it
          // would prove against a bad key and fail on-chain verification, so
          // evict it and fall through to a fresh download.
          const want = expectedSize(url);
          if (want != null && bytes.length !== want) {
            await store.delete(url);
          } else {
            cache.set(url, bytes);
            return bytes;
          }
        }
      } catch {
        // A cache read failure must not block proving; fall through to network.
      }
    }

    const result = await fetchRanged(url, onProgress);
    // Integrity gate before caching: never persist or use a buffer whose length
    // does not match the expected asset. A wrong-size key would otherwise be
    // saved and reused, proving fine in the browser but failing on chain.
    const want = expectedSize(url);
    if (want != null && result.length !== want) {
      throw new Error(
        `could not download proving asset (size mismatch: got ${result.length}, expected ${want})`,
      );
    }
    cache.set(url, result);

    // Persist for next reload. Best-effort: quota or storage failures are
    // swallowed so they never break this session's proof.
    if (store) {
      try {
        await store.put(
          url,
          // Cast: TS 5.7 narrows Uint8Array's buffer to ArrayBufferLike, which
          // no longer matches BodyInit's BufferSource, though the value is a
          // valid body at runtime.
          new Response(result as unknown as BodyInit, {
            headers: {
              "Content-Length": String(result.length),
              "Content-Type": "application/octet-stream",
            },
          }),
        );
      } catch {
        // ignore: the in-memory copy still serves this session.
      }
    }
    return result;
  })();

  inflight.set(url, job);
  try {
    return await job;
  } finally {
    inflight.delete(url);
  }
}

export interface ProvingAssets {
  wasm: Uint8Array;
  zkey: Uint8Array;
}

export async function loadProvingAssets(
  onProgress?: DownloadProgress,
): Promise<ProvingAssets> {
  // The wasm is small, so only the large proving key drives reported progress.
  const wasm = await fetchBytes(wasmUrl());
  const zkey = await fetchBytes(zkeyUrl(), onProgress);
  return { wasm, zkey };
}

// Drop the in-memory copies of the proving assets. The disk Cache Storage copy
// is kept, so the next load re-reads from disk without re-downloading. Called
// after the buffers are transferred into the prover worker: a transfer detaches
// the underlying ArrayBuffer, leaving the in-memory entries unusable.
export function invalidateProvingMemoryCache(): void {
  cache.clear();
}

// Drop EVERY copy of the proving assets, in memory and on disk, so the next
// load re-downloads a fresh build. Used as a self-heal: a proof that builds but
// fails on-chain verification points at a stale or corrupt cached key (a wrong
// key still proves), and clearing the disk cache lets the retry recover without
// the user clearing site data by hand. Best-effort and safe to call any time;
// it never throws.
export async function clearProvingCache(): Promise<void> {
  cache.clear();
  try {
    if (typeof caches !== "undefined") await caches.delete(ASSET_CACHE);
  } catch {
    // ignore: a failed cache delete still leaves the in-memory copy cleared.
  }
}

// Cheap check of whether both proving assets are already persisted on disk at
// the expected size. It reads only the stored Content-Length header, never the
// ~215MB body, so it is safe to call on every Private-tab open. Used to skip a
// redundant prewarm.
async function assetsCachedOnDisk(): Promise<boolean> {
  try {
    const store = await openAssetCache();
    if (!store) return false;
    for (const url of [wasmUrl(), zkeyUrl()]) {
      const hit = await store.match(url);
      if (!hit || !hit.ok) return false;
      const want = expectedSize(url);
      const len = Number(hit.headers.get("content-length") || 0);
      if (want != null && len !== want) return false;
    }
    return true;
  } catch {
    return false;
  }
}

// Warm the disk cache ahead of the first proof. Called when the user opens a
// surface that proves (the Private tab) so the ~215MB proving key is
// downloading in the background while they fill in the form, instead of only
// starting when they click. It is best-effort and silent: any failure changes
// nothing and the real proof attempt still reports its own progress and errors.
// Only call this where proving is actually allowed (a capable standalone tab),
// so visitors on phones or inside an embedded frame are not made to pull 215MB
// they can never use.
export async function prewarmProvingAssets(): Promise<void> {
  try {
    // Already persisted at the right size: nothing to do. Re-reading the key off
    // disk into memory just to drop it again would waste the read every time.
    if (await assetsCachedOnDisk()) return;
    // Download to disk. The return value is intentionally discarded; the point
    // is to populate the shared Cache Storage so the prover worker (a separate
    // isolate with its own in-memory map) reads from disk with no network when
    // the user proves.
    await loadProvingAssets();
  } catch {
    // ignore: prewarm is opportunistic; the proof attempt handles real errors.
  } finally {
    // Free the prewarmed buffers from THIS (main-thread) isolate; the disk copy
    // is what the worker reads. Holding 215MB in the page alongside the worker's
    // ~1GB proof would needlessly raise the tab's peak memory.
    invalidateProvingMemoryCache();
  }
}
